'use client';

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { MessageCircle, X } from 'lucide-react';
import { useAction } from 'next-safe-action/hooks';
import { useForm } from 'react-hook-form';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';

import { sendWidgetContactEmail } from '~/(marketing)/contact/_lib/server/widget-server-actions';
import { WidgetContactSchema } from '~/(marketing)/contact/_lib/widget-contact.schema';

/**
 * Brand accent is read from CSS custom properties so a consumer can recolor
 * the widget without touching this component. Set them on any ancestor (the
 * page, a wrapper, `:root`, or `.site-chat-widget` itself):
 *
 *   .site-chat-widget { --site-chat-accent: #0F766E; --site-chat-accent-foreground: #fff; }
 *
 * When unset they fall back to the theme `--primary` / `--primary-foreground`,
 * so the default appearance is unchanged.
 */
const ACCENT_BG = 'bg-[var(--site-chat-accent,var(--primary))]';
const ACCENT_FG =
  'text-[var(--site-chat-accent-foreground,var(--primary-foreground))]';
const ACCENT_TEXT = 'text-[var(--site-chat-accent,var(--primary))]';

export function SiteChatWidget() {
  const [open, setOpen] = useState(false);

  return (
    <div className="site-chat-widget fixed right-6 bottom-6 z-50">
      {open ? (
        <ChatPanel onClose={() => setOpen(false)} />
      ) : (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          className={`${ACCENT_BG} ${ACCENT_FG} flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 focus:outline-none`}
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}

function ChatPanel({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const { execute, isPending } = useAction(sendWidgetContactEmail, {
    onSuccess: () => setSubmitted(true),
    onError: () => setError(true),
  });

  const form = useForm({
    resolver: zodResolver(WidgetContactSchema),
    defaultValues: { name: '', email: '', message: '' },
  });

  return (
    <div className="bg-card border-border flex w-[350px] flex-col overflow-hidden rounded-2xl border shadow-2xl">
      {/* Header */}
      <div
        className={`${ACCENT_BG} flex items-center justify-between px-4 py-3`}
      >
        <span className={`${ACCENT_FG} text-sm font-semibold`}>
          Chat with us
        </span>
        <button
          onClick={onClose}
          aria-label="Close chat"
          className={`${ACCENT_FG} opacity-70 transition-opacity hover:opacity-100`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="h-[390px] overflow-y-auto p-4">
        {submitted ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div
              className={`${ACCENT_TEXT} mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--site-chat-accent,var(--primary))_12%,transparent)]`}
            >
              <MessageCircle className="h-6 w-6" />
            </div>
            <p className="text-foreground font-medium">
              Thanks! We'll be back shortly.
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              Typical response within 1 business day.
            </p>
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <p className="text-destructive text-sm font-medium">
              Something went wrong.
            </p>
            <button
              onClick={() => setError(false)}
              className={`${ACCENT_TEXT} mt-2 text-xs underline`}
            >
              Try again
            </button>
          </div>
        ) : (
          <Form {...form}>
            <form
              className="flex flex-col gap-3"
              onSubmit={form.handleSubmit((data) => execute(data))}
            >
              <p className="text-muted-foreground text-xs">
                Send us a message and we'll get back to you.
              </p>

              <FormField
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Name (optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Your name"
                        maxLength={200}
                        className="h-8 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Email (optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="you@company.com"
                        className="h-8 text-sm"
                        data-mask
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Message</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="How can we help?"
                        maxLength={5000}
                        className="min-h-[100px] text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isPending}
                size="sm"
                className="w-full bg-[var(--site-chat-accent,var(--primary))] text-[var(--site-chat-accent-foreground,var(--primary-foreground))] hover:bg-[var(--site-chat-accent,var(--primary))] hover:opacity-90"
              >
                {isPending ? 'Sending...' : 'Send message'}
              </Button>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
